'use client';

import { useState, useEffect } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { LanguageSelector } from './language-selector';
import { CurrencySelector } from './currency-selector';

interface PublicNavProps {
  locale: string;
}

export function PublicNav({ locale }: PublicNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Features', href: `/${locale}/features` },
    { name: 'Pricing', href: `/${locale}/pricing` },
    { name: 'Solutions', href: `/${locale}/solutions` },
    { name: 'Resources', href: `/${locale}/resources` },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl shadow-2xl shadow-blue-500/5 border-b border-gray-200/50 dark:border-gray-800/50' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href={`/${locale}`} className="group flex items-center gap-3 relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl blur opacity-0 group-hover:opacity-75 transition-opacity duration-500" />
            <img 
              src="/logo-44.png" 
              alt="NextPAL Nexus" 
              className="h-10 group-hover:scale-110 transition-transform duration-300" 
            />
            <div className="relative">
              <div className="text-2xl font-black text-gray-900 dark:text-white group-hover:scale-105 transition-transform duration-300">
                NextPAL Nexus
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                Enterprise Backup Platform
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={`group relative px-5 py-2 text-sm font-semibold transition-all duration-300 ${
                  scrolled ? 'text-gray-700 dark:text-gray-300' : 'text-white'
                } hover:text-blue-600 dark:hover:text-blue-400`}
              >
                <span className="relative z-10">{item.name}</span>
                <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector currentLocale={locale} />
            <CurrencySelector />
            <Button 
              variant="ghost" 
              size="sm" 
              className={`font-semibold ${
                scrolled ? '' : 'text-white hover:text-white hover:bg-white/10'
              }`}
              asChild
            >
              <Link href="/login">Login</Link>
            </Button>
            <Button 
              size="sm" 
              className="relative group overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 hover:from-blue-700 hover:via-purple-700 hover:to-green-700 text-white font-bold px-6 h-10 shadow-lg hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link href="/signup">
                <span className="relative z-10">Start Free Trial</span>
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-xl transition-colors ${
              scrolled ? 'text-gray-900 dark:text-white' : 'text-white'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-gray-200/50 dark:border-gray-800/50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-2xl rounded-b-3xl">
            <div className="flex flex-col gap-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="px-4 py-3 text-base font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="flex items-center gap-2 px-4 pt-4 border-t border-gray-200 dark:border-gray-800 mt-2">
                <LanguageSelector currentLocale={locale} />
                <CurrencySelector />
              </div>
              <div className="flex flex-col gap-2 px-4 pt-4">
                <Button variant="ghost" size="sm" className="w-full justify-center" asChild>
                  <Link href="/login">Login</Link>
                </Button>
                <Button size="sm" className="w-full justify-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" asChild>
                  <Link href="/signup">Start Free Trial</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
